/**
 * Integrations tab inside Environment Settings.
 *
 * Surfaces super-admin-managed secrets that previously required an env var
 * + redeploy to rotate. Today: Google Workspace (client_id +
 * service-account JSON). Future: Stripe, Resend, etc. — drop in another
 * <SecretRow> per integration.
 *
 * Backend contract:
 *   GET   /api/admin/platform-settings/status
 *   POST  /api/admin/platform-settings/google-workspace
 *   DELETE /api/admin/platform-settings/{key}  body={confirm:true}
 *
 * Auth: super-admin only (route enforces).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Globe, ShieldCheck, AlertTriangle, Loader2, Trash2, Save, BookOpen, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SourceBadge = ({ status }) => {
  if (!status) return null;
  if (status.source === 'db') {
    return (
      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="source-badge-db">
        <ShieldCheck className="w-3 h-3 mr-1" />
        Configured in DB
      </Badge>
    );
  }
  if (status.source === 'env') {
    return (
      <Badge className="bg-slate-50 text-slate-700 border-slate-200" data-testid="source-badge-env">
        Inherited from env var
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-50 text-amber-700 border-amber-200" data-testid="source-badge-unset">
      <AlertTriangle className="w-3 h-3 mr-1" />
      Not configured
    </Badge>
  );
};

// =========================================================================
// SetupGuideDrawer — right-side help panel covering the full GCP + DWD flow.
// =========================================================================
// Mirrors the operator-friendly walkthrough we send customers on first
// onboarding. Numbered, with direct console URLs as buttons. Self-contained
// so we can also surface it from a "Help" affordance elsewhere later.
const _SCOPES_TEXT =
  'https://www.googleapis.com/auth/admin.directory.user.readonly,' +
  'https://www.googleapis.com/auth/admin.directory.group.readonly,' +
  'https://www.googleapis.com/auth/admin.directory.group.member.readonly,' +
  'https://www.googleapis.com/auth/admin.directory.orgunit.readonly,' +
  'https://www.googleapis.com/auth/admin.directory.rolemanagement.readonly,' +
  'https://www.googleapis.com/auth/admin.directory.domain.readonly,' +
  'https://www.googleapis.com/auth/admin.directory.customer.readonly,' +
  'https://www.googleapis.com/auth/admin.directory.userschema.readonly,' +
  'https://www.googleapis.com/auth/admin.reports.audit.readonly,' +
  'https://www.googleapis.com/auth/admin.reports.usage.readonly';

const LinkBtn = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 underline decoration-emerald-300 hover:decoration-emerald-700 text-sm font-medium"
  >
    {children}
    <ExternalLink className="w-3 h-3" />
  </a>
);

const Step = ({ n, title, children }) => (
  <div className="mb-4">
    <div className="flex items-start gap-2.5 mb-1">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-semibold flex-shrink-0">
        {n}
      </span>
      <div className="text-sm font-semibold text-slate-900 pt-0.5">{title}</div>
    </div>
    <div className="text-sm text-slate-700 pl-8 leading-relaxed space-y-1.5">
      {children}
    </div>
  </div>
);

const SetupGuideDrawer = () => {
  const copyScopes = async () => {
    try {
      await navigator.clipboard.writeText(_SCOPES_TEXT);
      toast.success('Scopes copied — paste into Admin Console');
    } catch {
      toast.error('Copy failed. Select the text manually and copy.');
    }
  };
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="setup-guide-trigger"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-900 underline decoration-emerald-300 hover:decoration-emerald-700"
        >
          <BookOpen className="w-3.5 h-3.5" />
          How to set this up
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="setup-guide-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-700" />
            Google Workspace setup — step-by-step
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4">
          <Alert className="bg-slate-50 border-slate-200">
            <AlertDescription className="text-xs text-slate-700 leading-relaxed">
              Nova uses a <strong>Service Account with Domain-Wide Delegation</strong>,
              not interactive OAuth — so there's <strong>no redirect URL</strong> in this
              flow. The only "URL" inputs are the scope strings you paste into the
              Workspace Admin Console (step 6 below).
            </AlertDescription>
          </Alert>
        </div>

        {/* ---- Part 1 ---- */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
            Part 1 · Google Cloud (one-time)
          </div>

          <Step n={1} title="Create (or reuse) a Google Cloud project">
            <LinkBtn href="https://console.cloud.google.com/projectcreate">
              console.cloud.google.com/projectcreate
            </LinkBtn>
            <div>Name it <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">Nova Platform</code> (anything works).
            Pick your Workspace org. Make sure the new project is selected in the top blue bar.</div>
          </Step>

          <Step n={2} title="Enable the Admin SDK API">
            <LinkBtn href="https://console.cloud.google.com/apis/library/admin.googleapis.com">
              console.cloud.google.com/apis/library/admin.googleapis.com
            </LinkBtn>
            <div>Click <strong>Enable</strong>. This is the <strong>only API</strong> you need —
            no Gmail / Drive / Calendar API enable required.</div>
          </Step>

          <Step n={3} title="Create the Service Account">
            <LinkBtn href="https://console.cloud.google.com/iam-admin/serviceaccounts/create">
              console.cloud.google.com/iam-admin/serviceaccounts/create
            </LinkBtn>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Name: <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">ethira-platform-sa</code></li>
              <li>Description: "Read-only directory access for Nova compliance evidence"</li>
              <li><strong>Grant access</strong> (step 2 inside Google's wizard) → skip → Continue</li>
              <li><strong>Grant users</strong> (step 3) → skip → Done</li>
            </ul>
            <div className="text-xs text-slate-500">DWD doesn't use GCP IAM roles — both grant steps are intentionally skipped.</div>
          </Step>

          <Step n={4} title="Create the JSON key">
            <div>On the service account detail page → <strong>Keys</strong> tab → <strong>Add Key</strong> →
            <strong> Create new key</strong> → <strong>JSON</strong> → <strong>Create</strong>.</div>
            <div>A <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">.json</code> file downloads automatically.
            <span className="text-amber-700 font-medium"> Treat it like a database password.</span></div>
          </Step>

          <Step n={5} title="Copy the Client ID (21-digit number)">
            <div>On the service-account detail page, copy the <strong>Unique ID</strong> field
            (looks like <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">104626727666218881097</code>).
            Same value is in <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">client_id</code> inside the JSON you just downloaded.</div>
          </Step>
        </div>

        {/* ---- Part 2 ---- */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
            Part 2 · Workspace Admin Console (one-time)
          </div>

          <Step n={6} title="Authorize Domain-Wide Delegation">
            <LinkBtn href="https://admin.google.com/ac/owl/domainwidedelegation">
              admin.google.com/ac/owl/domainwidedelegation
            </LinkBtn>
            <div className="text-xs text-slate-500">
              (Or: admin.google.com → Security → Access and data control → API controls → MANAGE DOMAIN WIDE DELEGATION.)
            </div>
            <div className="mt-1">Click <strong>Add new</strong>, then:</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li><strong>Client ID</strong>: paste the 21-digit number from step 5</li>
              <li><strong>OAuth Scopes</strong>: paste the scope list below (every scope is <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">.readonly</code> — Nova can never write)</li>
              <li>Click <strong>Authorize</strong></li>
            </ul>
            <div className="mt-2">
              <button
                type="button"
                onClick={copyScopes}
                data-testid="copy-scopes-btn"
                className="text-xs px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                Copy scopes to clipboard
              </button>
            </div>
            <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded font-mono text-[10px] text-slate-700 break-all leading-relaxed">
              {_SCOPES_TEXT}
            </div>
          </Step>
        </div>

        {/* ---- Part 3 ---- */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
            Part 3 · Paste into Nova (this screen)
          </div>

          <Step n={7} title="Fill the form below this drawer">
            <ul className="list-disc list-inside space-y-0.5">
              <li><strong>Client ID</strong>: the 21-digit number from step 5</li>
              <li><strong>Service Account JSON</strong>: full content of the file from step 4 (must start with <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">{'{"type":"service_account",…'}</code>)</li>
              <li><strong>Test Admin Email</strong>: a real super-admin in your Workspace (e.g. <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">admin@yourdomain.com</code>)</li>
              <li>Keep <strong>Run live test</strong> checked</li>
              <li>Click <strong>Save</strong></li>
            </ul>
            <div>Nova will impersonate the test admin and make one <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">Admin SDK users.list(maxResults=1)</code> call.
            Save only succeeds if Google returns 200. DWD changes can take 1–5 minutes to propagate — wait + retry if needed.</div>
          </Step>
        </div>

        {/* ---- Troubleshooting ---- */}
        <div className="mt-6 mb-8">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
            Troubleshooting
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2 font-semibold text-slate-700">If you see…</th>
                  <th className="text-left p-2 font-semibold text-slate-700">Do this</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="p-2 font-mono text-[11px] text-red-700">unauthorized_client</td>
                  <td className="p-2">Step 6 was missed OR wrong Client ID was pasted. Re-check; wait 1–5 min for propagation.</td>
                </tr>
                <tr>
                  <td className="p-2 font-mono text-[11px] text-red-700">Not Authorized to access this resource/api</td>
                  <td className="p-2">Scopes missing or misspelled. Re-paste the full scope list (use the Copy button above).</td>
                </tr>
                <tr>
                  <td className="p-2 font-mono text-[11px] text-red-700">Domain not found</td>
                  <td className="p-2">Test Admin Email isn't a real super-admin. Use the actual admin@yourdomain.com.</td>
                </tr>
                <tr>
                  <td className="p-2 font-mono text-[11px] text-red-700">Invalid JSON / missing required fields</td>
                  <td className="p-2">Re-copy the entire JSON file — brace to brace.</td>
                </tr>
                <tr>
                  <td className="p-2 font-mono text-[11px] text-red-700">403 Forbidden when saving</td>
                  <td className="p-2">Your Nova account isn't a super admin. Have one save it.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const IntegrationsTab = () => {
  const [statusMap, setStatusMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Form fields. All optional — backend lets us update one at a time.
  const [clientId, setClientId] = useState('');
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [testAdminEmail, setTestAdminEmail] = useState('');
  const [runLiveTest, setRunLiveTest] = useState(true);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/platform-settings/status`, {
        headers: authHeaders(),
      });
      setStatusMap(r.data.settings || {});
    } catch (e) {
      toast.error(`Failed to load platform settings: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const onSave = async () => {
    if (!clientId.trim() && !serviceAccountJson.trim()) {
      toast.error('Nothing to save — provide a Client ID or Service Account JSON.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        run_live_test: runLiveTest,
      };
      if (clientId.trim()) body.client_id = clientId.trim();
      if (serviceAccountJson.trim()) body.service_account_json = serviceAccountJson.trim();
      if (testAdminEmail.trim()) body.test_admin_email = testAdminEmail.trim();
      const r = await axios.post(
        `${API_URL}/api/admin/platform-settings/google-workspace`,
        body,
        { headers: authHeaders() },
      );
      toast.success(
        r.data.live_test_run
          ? `Saved + live-tested ${r.data.saved.length} setting(s)`
          : `Saved ${r.data.saved.length} setting(s)`
      );
      // Clear the JSON field (it's now encrypted in DB; never re-display it).
      setServiceAccountJson('');
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (key) => {
    if (!window.confirm(`Remove the DB override for ${key}? The backend will fall back to the env var.`)) {
      return;
    }
    setDeleting(key);
    try {
      await axios.delete(`${API_URL}/api/admin/platform-settings/${key}`, {
        headers: authHeaders(),
        data: { confirm: true },
      });
      toast.success(`Removed DB override for ${key}`);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-8" data-testid="integrations-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading platform integrations…
      </div>
    );
  }

  const gwClient = statusMap?.GOOGLE_WS_OAUTH_CLIENT_ID;
  const gwJson = statusMap?.GOOGLE_WS_SERVICE_ACCOUNT_JSON;

  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-emerald-50">
                <Globe className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <CardTitle className="text-base">Google Workspace · Domain-Wide Delegation</CardTitle>
                <CardDescription>
                  These are Nova's platform credentials (one set for the whole platform — every customer
                  pastes the same Client ID into their own Admin Console). Encrypted at rest with Fernet.
                </CardDescription>
                <div className="mt-2">
                  <SetupGuideDrawer />
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Status row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-lg p-3" data-testid="gw-client-id-status">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold text-slate-700">CLIENT ID</Label>
                <SourceBadge status={gwClient} />
              </div>
              <div className="text-sm text-slate-900 font-mono">
                {gwClient?.preview || '—'}
              </div>
              {gwClient?.db_updated_at && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Updated {new Date(gwClient.db_updated_at).toLocaleString()}
                </div>
              )}
              {gwClient?.source === 'db' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 text-xs text-red-600 hover:bg-red-50"
                  onClick={() => onDelete('GOOGLE_WS_OAUTH_CLIENT_ID')}
                  disabled={deleting === 'GOOGLE_WS_OAUTH_CLIENT_ID'}
                  data-testid="gw-client-id-delete"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Remove override
                </Button>
              )}
            </div>
            <div className="border border-slate-200 rounded-lg p-3" data-testid="gw-sa-json-status">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold text-slate-700">SERVICE ACCOUNT JSON</Label>
                <SourceBadge status={gwJson} />
              </div>
              <div className="text-sm text-slate-900">
                {gwJson?.configured ? 'Stored securely (encrypted)' : '—'}
              </div>
              {gwJson?.db_updated_at && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Updated {new Date(gwJson.db_updated_at).toLocaleString()}
                </div>
              )}
              {gwJson?.source === 'db' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 text-xs text-red-600 hover:bg-red-50"
                  onClick={() => onDelete('GOOGLE_WS_SERVICE_ACCOUNT_JSON')}
                  disabled={deleting === 'GOOGLE_WS_SERVICE_ACCOUNT_JSON'}
                  data-testid="gw-sa-json-delete"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Remove override
                </Button>
              )}
            </div>
          </div>

          {/* Edit form */}
          <div className="border-t pt-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              Update credentials
            </div>
            <Alert className="mb-4 bg-amber-50 border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <AlertDescription className="text-xs text-amber-800">
                The service-account JSON is the master credential that lets Nova read every connected
                customer's Workspace directory. Treat it like a database password. Encrypted at rest;
                never displayed back after save.
              </AlertDescription>
            </Alert>
            <div className="grid gap-4">
              <div>
                <Label htmlFor="gw-client-id" className="text-xs">
                  Client ID
                  <span className="text-slate-400 font-normal ml-1">
                    (numeric `client_id` field from the SA JSON)
                  </span>
                </Label>
                <Input
                  id="gw-client-id"
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder={gwClient?.preview ? `Current: ${gwClient.preview}` : '1234567890…'}
                  className="font-mono text-sm"
                  data-testid="gw-client-id-input"
                />
              </div>
              <div>
                <Label htmlFor="gw-sa-json" className="text-xs">
                  Service Account JSON
                  <span className="text-slate-400 font-normal ml-1">
                    (paste the full JSON content)
                  </span>
                </Label>
                <Textarea
                  id="gw-sa-json"
                  value={serviceAccountJson}
                  onChange={(e) => setServiceAccountJson(e.target.value)}
                  placeholder='{"type":"service_account","client_id":"…","private_key":"-----BEGIN PRIVATE KEY-----…","client_email":"…"}'
                  className="font-mono text-xs h-40"
                  data-testid="gw-sa-json-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="gw-test-email" className="text-xs">
                    Test Admin Email
                    <span className="text-slate-400 font-normal ml-1">
                      (required if live-testing JSON)
                    </span>
                  </Label>
                  <Input
                    id="gw-test-email"
                    type="email"
                    value={testAdminEmail}
                    onChange={(e) => setTestAdminEmail(e.target.value)}
                    placeholder="admin@yourdomain.com"
                    data-testid="gw-test-email-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={runLiveTest}
                      onChange={(e) => setRunLiveTest(e.target.checked)}
                      data-testid="gw-run-live-test"
                      className="rounded border-slate-300"
                    />
                    Run live test against Google before saving (recommended)
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  onClick={onSave}
                  disabled={saving}
                  data-testid="gw-save-btn"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsTab;
